
import { useEffect, useState, useCallback } from "react";
import { useTestCreator, TestDetails } from "@/hooks/useTestCreator";
import { TestCreatorHeaderSection } from "./test-creator-sections/TestCreatorHeader";
import { TestDetailsScreen } from "./screens/TestDetailsScreen";
import { QuestionMakerScreen } from "./screens/QuestionMakerScreen";
import { ReviewScreen } from "./screens/ReviewScreen";
import { SuccessScreen } from "./screens/SuccessScreen";
import { Question } from "@/types/test";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { regenerateQuestionWithFeedback } from "./utils/questionGeneration";
import { useIsMobile } from "@/hooks/use-mobile";

interface TestCreatorContainerProps {
  initialDetails?: TestDetails;
  initialQuestions?: Question[];
  onTestCreated?: (testId: string) => void;
  startAtQuestionMaker?: boolean;
  skipToReview?: boolean;
}

const TestCreatorContainer = ({
  initialDetails,
  initialQuestions = [],
  onTestCreated,
  startAtQuestionMaker = false,
  skipToReview = false
}: TestCreatorContainerProps) => {
  console.log('🚀 TestCreatorContainer initializing with:', {
    initialDetails,
    initialQuestions
  });
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(
    skipToReview ? 2 : startAtQuestionMaker ? 1 : 0
  );
  const [publishedTestId, setPublishedTestId] = useState<string | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const isMobile = useIsMobile();
  const {
    testDetails,
    setTestDetails,
    questions,
    setQuestions,
    addQuestion: addQuestionHook,
    updateQuestion: updateQuestionHook,
    deleteQuestion: deleteQuestionHook,
    addMultipleQuestions: addMultipleQuestionsHook,
    saveTest: saveTestHook
  } = useTestCreator(initialDetails, initialQuestions);

  useEffect(() => {
    if (initialQuestions?.length > 0) {
      console.log('📥 Initial questions passed to useTestCreator:', initialQuestions.length);
      // No need to call setQuestions here since we're now passing initialQuestions to useTestCreator
    }
  }, [initialQuestions]);

  const steps = ["Test Details", "Question Maker", "Review & Finalize"];
  const progress = (currentStep + 1) / steps.length * 100;

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      console.log(`⏭️ Moving to step ${currentStep + 1}`);
      setCurrentStep(currentStep + 1);
      // Scroll to top when changing steps
      window.scrollTo(0, 0);
    }
  }, [currentStep, steps.length]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      console.log(`⏮️ Moving to step ${currentStep - 1}`);
      setCurrentStep(currentStep - 1);
      // Scroll to top when changing steps
      window.scrollTo(0, 0);
    }
  }, [currentStep]);
  
  const handleBackNavigation = useCallback(() => {
    // If we're on the first step, go back to test management
    if (currentStep === 0) {
      navigate('/test-management');
    } else {
      // Otherwise, go to the previous step
      handlePrevious();
    }
  }, [currentStep, handlePrevious, navigate]);

  const addQuestion = useCallback(() => {
    console.log('➕ Adding new question');
    const newQuestionId = addQuestionHook();
    return newQuestionId;
  }, [addQuestionHook]);

  const updateQuestion = useCallback((id: string, updates: Partial<Question>) => {
    console.log('🔄 Updating question:', id, updates);
    updateQuestionHook(id, updates);
  }, [updateQuestionHook]);

  const deleteQuestion = useCallback((id: string) => {
    console.log('🗑️ Deleting question:', id);
    deleteQuestionHook(id);
  }, [deleteQuestionHook]);

  const handleSaveTest = useCallback(async (publish: boolean, updatedDetails?: Partial<TestDetails>) => {
    try {
      console.log("💾 Saving test with publish =", publish);
      console.log("💾 Updated details =", updatedDetails);
      
      if (publish) {
        setIsPublishing(true);
      }
      
      // If updatedDetails is provided, merge with current testDetails
      const finalDetails = updatedDetails 
        ? { ...testDetails, ...updatedDetails }
        : testDetails;
      
      console.log("💾 Final details for saving:", finalDetails);
      
      // Save test with final details
      const result = await saveTestHook(finalDetails, questions, publish);
      
      if (publish && result?.id) {
        console.log(`✅ Test published successfully with ID: ${result.id}`);
        setPublishedTestId(result.id);
        
        if (onTestCreated) {
          console.log("📞 Calling onTestCreated callback with ID:", result.id);
          onTestCreated(result.id);
        }
      } else if (!publish && result?.id) {
        // For drafts, still navigate to test management
        navigate('/test-management');
      }
      return result;
    } catch (error) {
      console.error("Error saving test:", error);
      throw error;
    } finally {
      setIsPublishing(false);
    }
  }, [saveTestHook, testDetails, questions, onTestCreated, navigate]);

  const handleGenerateQuestions = useCallback((generatedQuestions: Question[]) => {
    console.log('📝 Setting generated questions:', generatedQuestions);
    setQuestions(generatedQuestions);
  }, [setQuestions]);

  const handleAddMultipleQuestions = useCallback((newQuestions: Question[]) => {
    console.log('📝 Adding multiple questions:', newQuestions.length);
    addMultipleQuestionsHook(newQuestions);
  }, [addMultipleQuestionsHook]);

  const handleRegenerateQuestion = useCallback(async (questionId: string, feedback: string) => {
    const questionToRegenerate = questions.find(q => q.id === questionId);
    if (!questionToRegenerate) return;
    try {
      console.log('🔄 Regenerating question with feedback:', { questionId, feedback });
      const regeneratedQuestion = await regenerateQuestionWithFeedback(
        questionToRegenerate,
        feedback,
        testDetails.title, 
        testDetails.description, 
        testDetails.keyResponsibilities, 
        testDetails.yearsOfExperience, 
        testDetails.keySkills
      );
      console.log('✅ Question regenerated:', regeneratedQuestion);
      updateQuestion(questionId, regeneratedQuestion);
    } catch (error) {
      console.error('❌ Error regenerating question:', error);
      throw error;
    }
  }, [questions, testDetails, updateQuestion]);

  console.log('📊 Current state:', {
    currentStep,
    questions,
    publishedTestId
  });

  const handlePublish = useCallback(async () => {
    await handleSaveTest(true);
  }, [handleSaveTest]);

  const handleSaveAsDraft = useCallback(async () => {
    await handleSaveTest(false);
  }, [handleSaveTest]);

  if (publishedTestId) {
    console.log('✅ Rendering SuccessScreen for test ID:', publishedTestId);
    return (
      <div className="container max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        <SuccessScreen testId={publishedTestId} />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background flex flex-col">
      <div className={`w-full flex-grow ${isMobile ? 'pb-24' : ''}`}>
        <TestCreatorHeaderSection testId={null} onBackClick={handleBackNavigation} />
        
        <div className="mb-8">
          <div className="rounded-lg bg-card p-6 border border-border shadow-sm">
            <div className="flex flex-col space-y-2">
              {/* Step numbers and labels above progress bar */}
              <div className="flex justify-between items-center mb-2">
                {steps.map((step, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div 
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium mb-1", 
                        index <= currentStep 
                          ? "bg-secondary text-secondary-foreground" 
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {index < currentStep ? <Check className="h-4 w-4" /> : index + 1}
                    </div>
                    <span 
                      className={cn(
                        `text-sm font-medium ${isMobile ? 'text-xs' : ''}`, 
                        index <= currentStep ? "text-foreground" : "text-muted-foreground"
                      )}
                    >
                      {step}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Progress bar */}
              <div className="relative h-2 w-full bg-muted rounded-full overflow-hidden">
                <div 
                  className="absolute left-0 top-0 h-full bg-secondary transition-all duration-300 rounded-full" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8 mb-10">
          {currentStep === 0 && (
            <TestDetailsScreen 
              details={testDetails} 
              onChange={setTestDetails} 
              onNext={handleNext} 
              onGenerateQuestions={handleGenerateQuestions} 
            />
          )}
          {currentStep === 1 && (
            <QuestionMakerScreen 
              questions={questions} 
              onAddQuestion={addQuestion} 
              onUpdateQuestion={updateQuestion} 
              onDeleteQuestion={deleteQuestion} 
              onRegenerateQuestion={handleRegenerateQuestion}
              onAddMultipleQuestions={handleAddMultipleQuestions}
              onNext={handleNext} 
              onPrevious={handlePrevious}
              jobContext={{
                title: testDetails.title,
                description: testDetails.description,
                keySkills: testDetails.keySkills,
                yearsOfExperience: testDetails.yearsOfExperience
              }}
            />
          )}
          {currentStep === 2 && (
            <ReviewScreen 
              testDetails={testDetails} 
              questions={questions} 
              onBack={handlePrevious} 
              onPublish={handlePublish} 
              onSaveDraft={handleSaveAsDraft}
              isPublishing={isPublishing}
              isSaving={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TestCreatorContainer;
